import { FC, MouseEventHandler, memo, useState } from 'react';
import { Card } from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import ReactPlayer from 'react-player/lazy';
import CarouselArrow from '@/components/ui/CarouselArrow';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/Store';
import { useVideoCarouselStyles } from './VideoCarousel.styles';
import { Skeleton } from '../../dumb/Skeleton';

require("@/assets/css/carousel.css");

interface VideoCarouselProps {
    videos: Array<any>;
    showLoading?: boolean;
    showThumbnails: boolean;
}

const VideoCarousel: FC<VideoCarouselProps> = props => {

    const {
        videos,
        showLoading = false,
        showThumbnails
    } = props;

    const { classes: { video, arrows, leftArrow, rightArrow, thumbnail, videoSlide } } = useVideoCarouselStyles();

    const { settings } = useSelector((state: RootState) => state.settings);

    const customRenderItem = (item, props) => <item.type {...item.props} {...props} />;

    const getVideoThumb = (videoId) => `https://img.youtube.com/vi/${videoId}/default.jpg`;

    const renderArrowPrev = (onClickHandler: MouseEventHandler<HTMLButtonElement>, hasPrev: boolean, label: string) =>
        hasPrev && <CarouselArrow direction="left" onClick={onClickHandler} label={label} />;

    const renderArrowNext = (onClickHandler: MouseEventHandler<HTMLButtonElement>, hasNext: boolean, label: string) =>
        hasNext && <CarouselArrow direction="right" onClick={onClickHandler} label={label} />;

    function youtube_parser(url) {
        if (!url) return false;
        var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        var match = url.match(regExp);
        return (match && match[7].length == 11) ? match[7] : false;
    }

    const customRenderThumb = (children) =>
        children.map((item, idx) => {
            // Thumbnails come from the video list (slides are plain elements without a url prop).
            const videoId = youtube_parser(videos?.[idx] ?? "");

            return showLoading ? (
                <Skeleton key={idx} animation="wave" variant="rectangular" height={70} width={70} />
            ) : (
                <img
                    key={idx} // Place the key here
                    src={getVideoThumb(videoId)}
                    className={thumbnail}
                    alt={`video-thumbnail-${idx}`} // Add alt for accessibility
                />
            );
        });


    // Only the visible slide gets a live player (each embedded player runs its own scripts and timers); the others
    // show their thumbnail, as on mobile.
    const [current, setCurrent] = useState(0);
    // A render function (not a component defined in render), so a re-render does not remount a playing video.
    const renderYoutubeSlide = (url: string, index: number) => (
        <div className="carousel-wrapper" key={index}>
            {index === current ? (
                <ReactPlayer width="100%" height="100%" url={url} controls={true} />
            ) : (
                <img src={getVideoThumb(youtube_parser(url))} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
        </div>
    );

    // Loading: the same skeleton as the photo carousel instead of placeholder slides.
    if (showLoading) {
        return (
            <div className="pb-carousel-skeleton">
                <Skeleton animation="wave" variant="rectangular" className="pb-carousel-skeleton-main" />
            </div>
        );
    }

    // Same frameless 16:9 frame and arrows as the photo carousel.
    return (
        <Card className={video + " pb-carousel"} raised={true}>
            <Carousel
                thumbWidth={70}
                stopOnHover={true}
                infiniteLoop={true}
                autoPlay={false}
                emulateTouch={true}
                showStatus={false}
                useKeyboardArrows={true}
                showThumbs={true}
                dynamicHeight={false}
                showArrows={true}
                selectedItem={current}
                onChange={(index) => setCurrent(index)}
                renderItem={customRenderItem}
                renderArrowPrev={renderArrowPrev}
                renderArrowNext={renderArrowNext}
                renderThumbs={showThumbnails ? customRenderThumb : undefined}>
                {
                    showLoading ? (
                        []
                    ) : (
                        videos.map((videoUrl, idx) => (
                            renderYoutubeSlide(videoUrl, idx)
                        ))
                    )
                }
            </Carousel>
        </Card>
    )
}

export default memo(VideoCarousel)